import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  AI_BATCH_PAGE_SIZE,
  AiBatch,
  AiBatchListQuery,
  AiBatchQuota,
  SubmitAiBatchForm,
} from "../../interfaces/AiBatchObject";
import { getRequest, postRequest } from "../http/httpRequest";
import { ApiEnvelopeError, unwrapEnvelope } from "../helpers/ApiEnvelopeHelper";
import { buildSubmitAiBatchPayload } from "../converters/uiToBackend/AiBatchConvertor";
import {
  convertAiBatch,
  convertAiBatchList,
  convertAiBatchQuota,
} from "../converters/backendToUi/aiBatchConvertor";

/**
 * Client for AI batch image edits — `/api/v1/imagegen/batches` and the
 * per-business quota. Contract: interfaces/AiBatchObject.ts.
 *
 * Unlike most legacy endpoints, these return their REAL HTTP status on failure
 * (400/403/404/409/422/429/502/503) with the standard envelope as the body, so
 * unwrapEnvelope still parses the error and throws ApiEnvelopeError carrying the
 * backend's numeric code (e.g. "16013").
 *
 * The scope of the list is decided server-side from the token: a curator gets
 * their own batches, an admin gets everyone's.
 */
const BEARER = true;

/** Request-level error codes (AiImageBatchError 16001–16049). */
const AI_BATCH_ERROR = {
  FEATURE_DISABLED: "16001",
  UNAUTHENTICATED: "16002",
  ROLE_CHECK_FAILED: "16003",
  NOT_ALLOWED: "16004",
  BUSINESS_NOT_CLAIMED: "16005",
  CLAIM_CHECK_FAILED: "16006",
  COLLECTION_NOT_FOUND: "16010",
  NO_ELIGIBLE_PRODUCTS: "16011",
  TOO_MANY_ITEMS: "16012",
  BUSINESS_LIMIT_EXCEEDED: "16013",
  DAILY_LIMIT_EXCEEDED: "16014",
  BATCH_NOT_FOUND: "16015",
  NOT_BATCH_OWNER: "16016",
  INVALID_BATCH_STATE: "16017",
  PROVIDER_UNAVAILABLE: "16018",
  PRODUCT_ALREADY_IN_BATCH: "16019",
} as const;

/** Backend errorCode → user-facing copy. Codes not listed fall back to the
 *  server's message (safe and human-readable per the API contract). */
const FRIENDLY_MESSAGES: Record<string, string> = {
  [AI_BATCH_ERROR.FEATURE_DISABLED]: "Batch editing is switched off right now.",
  [AI_BATCH_ERROR.UNAUTHENTICATED]: "Your session has expired. Please log in again.",
  [AI_BATCH_ERROR.ROLE_CHECK_FAILED]: "We couldn't verify your access. Please try again.",
  [AI_BATCH_ERROR.CLAIM_CHECK_FAILED]: "We couldn't verify your access. Please try again.",
  [AI_BATCH_ERROR.NOT_ALLOWED]: "Only curators and admins can run batch edits.",
  [AI_BATCH_ERROR.BUSINESS_NOT_CLAIMED]:
    "You can only run batch edits for businesses you've claimed.",
  [AI_BATCH_ERROR.COLLECTION_NOT_FOUND]: "That collection no longer exists.",
  [AI_BATCH_ERROR.NO_ELIGIBLE_PRODUCTS]:
    "None of this collection's products can be edited for this business.",
  [AI_BATCH_ERROR.TOO_MANY_ITEMS]:
    "Too many products for one batch. Split the collection.",
  [AI_BATCH_ERROR.BUSINESS_LIMIT_EXCEEDED]:
    "Not enough batch edits left for this business.",
  [AI_BATCH_ERROR.DAILY_LIMIT_EXCEEDED]: "Daily limit reached — try again tomorrow.",
  [AI_BATCH_ERROR.BATCH_NOT_FOUND]: "This batch no longer exists.",
  [AI_BATCH_ERROR.NOT_BATCH_OWNER]:
    "Only the curator who submitted this batch or an admin can do that.",
  [AI_BATCH_ERROR.INVALID_BATCH_STATE]:
    "This action isn't available for the batch right now. Refresh and try again.",
  [AI_BATCH_ERROR.PROVIDER_UNAVAILABLE]:
    "The image service is busy. Please try again shortly.",
  [AI_BATCH_ERROR.PRODUCT_ALREADY_IN_BATCH]:
    "Some products were just added to another batch. Please try again.",
};

/** BUSINESS_LIMIT_EXCEEDED carries `{ limit, used, requested, remaining }` —
 *  worth spelling out, since "not enough" alone doesn't say by how much. */
function businessLimitMessage(details?: Record<string, unknown>): string | null {
  const remaining = Number(details?.remaining);
  const requested = Number(details?.requested);
  if (!Number.isFinite(remaining) || !Number.isFinite(requested)) return null;
  return `Only ${remaining} batch edits left for this business — this collection needs ${requested}.`;
}

/** Converts any error thrown by this service into a display string. */
export function toFriendlyAiBatchError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    if (error.errorCode === AI_BATCH_ERROR.BUSINESS_LIMIT_EXCEEDED) {
      const detailed = businessLimitMessage(error.additionalDetails);
      if (detailed) return detailed;
    }
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

/** `{AI_IMAGE_BATCHES}/{batchId}[/{action}]`. Encoded: the id comes from
 *  server data, not a literal. */
function batchPath(batchId: string, action?: "cancel" | "revert"): string {
  const base = `${ServiceEndpoints.AI_IMAGE_BATCHES}/${encodeURIComponent(batchId)}`;
  return action ? `${base}/${action}` : base;
}

/** POST /batches — returns the new batch plus `skipped[]`. */
async function submitBatch(form: SubmitAiBatchForm): Promise<AiBatch> {
  const response = await postRequest(
    ServiceEndpoints.AI_IMAGE_BATCHES,
    buildSubmitAiBatchPayload(form),
    BEARER
  );
  return convertAiBatch(await unwrapEnvelope<unknown>(response));
}

/**
 * GET /batches?status&before&limit — newest first. `before` is the previous
 * page's last `createdAt`; omitted for the first page.
 */
async function getBatches({ filter, before }: AiBatchListQuery): Promise<AiBatch[]> {
  const params = new URLSearchParams({
    status: filter,
    limit: String(AI_BATCH_PAGE_SIZE),
  });
  if (before) params.set("before", before);
  const response = await getRequest(
    `${ServiceEndpoints.AI_IMAGE_BATCHES}?${params.toString()}`,
    BEARER
  );
  return convertAiBatchList(await unwrapEnvelope<unknown>(response));
}

/** GET /batches/{id} — the batch with its `items[]`. */
async function getBatch(batchId: string): Promise<AiBatch> {
  const response = await getRequest(batchPath(batchId), BEARER);
  return convertAiBatch(await unwrapEnvelope<unknown>(response));
}

/** POST /batches/{id}/cancel — allowed while PREPARING / SUBMITTED. */
async function cancelBatch(batchId: string): Promise<AiBatch> {
  const response = await postRequest(batchPath(batchId, "cancel"), {}, BEARER);
  return convertAiBatch(await unwrapEnvelope<unknown>(response));
}

/** POST /batches/{id}/revert — removes the batch's images. Slots aren't refunded. */
async function revertBatch(batchId: string): Promise<AiBatch> {
  const response = await postRequest(batchPath(batchId, "revert"), {}, BEARER);
  return convertAiBatch(await unwrapEnvelope<unknown>(response));
}

/** GET /businesses/{businessId}/batch-quota. */
async function getBatchQuota(businessId: string): Promise<AiBatchQuota> {
  const response = await getRequest(
    `${ServiceEndpoints.AI_IMAGE_BATCH_QUOTA_BASE}/${encodeURIComponent(businessId)}/batch-quota`,
    BEARER
  );
  return convertAiBatchQuota(await unwrapEnvelope<unknown>(response));
}

const AiBatchApiService = {
  submitBatch,
  getBatches,
  getBatch,
  cancelBatch,
  revertBatch,
  getBatchQuota,
};

export default AiBatchApiService;
